'use client';

import { c as _c } from "react/compiler-runtime";
import { Alert, Stack, Typography } from '@mui/material';
import { type MPARequest, MPARequestStatus } from '@repo/api-gw-sdk';
import type { ReactNode } from 'react';
import { Loader } from '@/components/shared/loader';
import type { UpdatePreviewOutcome } from '@/data/dal/lakehouseUpdatePreview';
import { HistoryChanges } from '@/features/streamlake/pipelines/tabs/historyChanges';
import { getIngestionPipelineTarget } from './mpaStreams';
import { useStoredStreamUpdatePreview } from './useMpaStreams';
export const COMPARED_WITH_CURRENT_STREAM = 'Compared with the stream as it is now. If the stream changes before this request is applied, the result can differ.';
export const UNLISTED_CHANGES_NOTE = 'This change also updates settings the list does not show, such as resources, autoscaling or compaction tuning. See the raw request for them.';
export const PREVIEW_NOT_FOUND_NOTE = 'A change preview is not available: the stream may have been deleted, or this environment does not offer previews yet.';

// Requests still to be applied; a preview of an applied or closed one would
// compare it with a stream it may already have changed.
const PREVIEWED_STATUSES = new Set<MPARequestStatus>([MPARequestStatus.MPA_REQUEST_STATUS_CREATED, MPARequestStatus.MPA_REQUEST_STATUS_PENDING_APPROVAL, MPARequestStatus.MPA_REQUEST_STATUS_APPROVED]);
const STREAM_RESOURCE_PATH = /\/ingestion-pipeline\/[^/?]+$/;

/**
 * Whether a stream request gets the change preview: a partial update of the
 * stream itself (PATCH on its own path) not yet applied. Every stream call,
 * delete, start, stop and cursor reset included, carries the Update stream
 * operation, so the stored method and path tell the update apart. The preview
 * takes PATCH bodies only, so a PUT keeps the raw view.
 */
export const previewsStreamUpdate = (request: MPARequest): boolean => request.requestMethod.toUpperCase() === 'PATCH' && STREAM_RESOURCE_PATH.test(request.requestPath) && Boolean(request.requestBody) && getIngestionPipelineTarget(request) !== null && PREVIEWED_STATUSES.has(request.status);
const fallbackNote = (outcome: Exclude<UpdatePreviewOutcome, {
  kind: 'changes' | 'rejected';
}>): string => {
  switch (outcome.kind) {
    case 'notFound':
      return PREVIEW_NOT_FOUND_NOTE;
    case 'diffUnavailable':
      return 'A change preview is not available for this type of stream.';
    case 'forbidden':
      return 'You need permission to update this stream to preview its changes.';
    case 'failed':
      return `The changes could not be previewed: ${outcome.message}`;
  }
};
export const StreamUpdatePreviewView = t0 => {
  const $ = _c(17);
  const {
    outcome,
    rawRequest
  } = t0;
  if (outcome.kind !== "changes") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        gap: "16px"
      };
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== outcome) {
      t2 = outcome.kind === "rejected" ? <Alert severity="error" data-testid="mpa-stream-update-rejected">{`This change would be rejected now: ${outcome.message}`}</Alert> : <Alert severity="info" data-testid="mpa-stream-update-raw-fallback">{fallbackNote(outcome)}</Alert>;
      $[1] = outcome;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== rawRequest || $[4] !== t2) {
      t3 = <Stack className="flex-1 min-h-0 overflow-auto" sx={t1}>{t2}{rawRequest}</Stack>;
      $[3] = rawRequest;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let t1;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: "16px"
    };
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Typography variant="body2">{COMPARED_WITH_CURRENT_STREAM}</Typography>;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== outcome.hasUnlistedChanges) {
    t3 = outcome.hasUnlistedChanges && <Alert severity="warning" data-testid="mpa-stream-update-unlisted">{UNLISTED_CHANGES_NOTE}</Alert>;
    $[8] = outcome.hasUnlistedChanges;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== outcome.after || $[11] !== outcome.changes || $[12] !== outcome.hasUnlistedChanges) {
    t4 = outcome.changes.length || !outcome.hasUnlistedChanges ? <HistoryChanges changes={outcome.changes} snapshot={outcome.after} /> : null;
    $[10] = outcome.after;
    $[11] = outcome.changes;
    $[12] = outcome.hasUnlistedChanges;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t3 || $[15] !== t4) {
    t5 = <Stack className="flex-1 min-h-0 overflow-auto" sx={t1} data-testid="mpa-stream-update-changes">{t2}{t3}{t4}</Stack>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
};
export const StreamUpdatePreview = t0 => {
  const $ = _c(4);
  const {
    request,
    rawRequest
  } = t0;
  const {
    data,
    isValidating
  } = useStoredStreamUpdatePreview(request);
  if (isValidating) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Loader />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== data || $[2] !== rawRequest) {
    t1 = data ? <StreamUpdatePreviewView outcome={data} rawRequest={rawRequest} /> : rawRequest;
    $[1] = data;
    $[2] = rawRequest;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};